/**
 * @module cache
 * @description Utility functions for Apollo Client cache configuration
 */

import {
  xSegVar, countryVar, userTypeVar, signifydIdVar,
} from '../lib/context/ApplicationContext/ApplicationContext';
import getFrontendId from './getFrontendId';

/**
 * @function getCacheId
 * @description Generates a cache ID for configuration data based on frontend information
 *
 * @param {Object} frontend - Frontend configuration object
 * @param {string} [id] - Optional identifier to append to the cache ID
 * @returns {string} Formatted cache ID string
 *
 * @example
 * const cacheId = getCacheId({storeId: '10001'}, 'user');
 * // Returns: "10001-user-config"
 */
export const getCacheId = (frontend, id) => {
  if (!id) return `${getFrontendId(frontend)}-config`;
  return `${getFrontendId(frontend, id)}-config`;
};

/*
  Note: removing use of defaultMerge because equivalent statement can be done by just using
  `merge: true`
  Found here: https://www.apollographql.com/docs/react/caching/cache-field-behavior#merging-non-normalized-objects
  ***
*/

/**
 * @function getInMemoryCacheSettings
 * @description Creates Apollo Client cache configuration with type policies
 *
 * @returns {Object} Cache configuration object with typePolicies
 *
 * @example
 * const { cache } = new ApolloClient({
 *   cache: new InMemoryCache(getInMemoryCacheSettings())
 * });
 */
export const getInMemoryCacheSettings = () => ({
  typePolicies: {
    /**
     * TextFor type policy - uses default merge strategy for non-normalized objects
     */
    TextFor: { merge: true },

    /**
     * ConfigValue type policy - uses default merge strategy for non-normalized objects
     */
    ConfigValue: { merge: true },

    /**
     * UserState type policy - uses custom keyFields function to generate unique identifiers
     */
    UserState: {
      merge: true,
      keyFields: ({ sessionId } = {}) => {
        if (!sessionId) return null;
        return `UserState:${sessionId}`;
      },
    },

    /**
     * OrderHistoryItem type policy - identifies items by order number
     */
    OrderHistoryItem: { keyFields: ['number'] },

    /**
     * UserInfo type policy - uses default merge strategy for non-normalized objects
     */
    UserInfo: { merge: true },

    /**
     * Query type policy - defines field policies for root query fields
     */
    Query: {
      fields: {
        /**
         * featureFlag field policy - uses key as the cache key
         */
        featureFlag: { keyArgs: ['key'] },

        /**
         * xSeg field policy - reads value from reactive variable
         */
        xSeg: {
          read() { return xSegVar(); },
        },

        /**
         * userCountry field policy - reads value from reactive variable
         */
        userCountry: {
          read() { return countryVar(); },
        },

        /**
         * userType field policy - reads value from reactive variable
         */
        userType: {
          read() { return userTypeVar(); },
        },

        /**
         * signifydId field policy - reads value from reactive variable
         */
        signifydId: {
          read() { return signifydIdVar(); },
        },
      },
    },
  },
});
